import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { ICampaignsGoals } from '../../../model/campaign/ICampaignsGoals';
import { Pagination } from '../../../shared/components/Pagination';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';
import ToggleChip from '../../../shared/components/ToggleChip';
import { CampaignsService } from '../CampaignsService';
import { ErrorToast } from '../../../utils/toasters';

const GOAL_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Title' },
    { label: 'Summary' },
    { label: 'Story' },
    { label: 'Active' },
    { label: 'Image' },
    { label: 'Edit' },
];

export function CampaignGoals() {
    const [state, setState] = useState({
        page: 1,
        perPage: 15,
        title: '',
    });

    const [campaignGoal, setCampaignGoal] = useState<ICampaignsGoals[]>([]);
    const [change, setChange] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [dataLoading, setDataLoading] = useState(true);

    useEffect(() => {
        setDataLoading(true);
        CampaignsService.getCampaignsGoals(state)
            .then(data => {
                setCampaignGoal(data.data);
                setTotalPages(Math.ceil(data.count / state.perPage));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [change, page]);

    function handleClick(event: any) {
        event.preventDefault();
        setChange(!change);
    }

    function handleChange(event: any) {
        const value = event.target.value.trim();
        setState({
            ...state,
            [event.target.name]: value,
        });
    }

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setPage(updatePage);
    };

    function handleCheckbox(id: number) {
        CampaignsService.campaignGoalActive(id)
            .then(() => {
                setCampaignGoal(prev =>
                    prev.map(goal => (goal.id === id ? { ...goal, isActive: !goal.isActive } : goal))
                );
            })
            .catch(err => ErrorToast(err));
    }

    return (
        <>
            <NavLink
                className="btn btn-outline-primary"
                to={{
                    pathname: '/campaign/goals/form/',
                }}
            >
                + Add campaign goal
            </NavLink>
            <Card
                className="mb-5 p-4 mt-3"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick(e);
                }}
            >
                <Row>
                    <Form.Group as={Col} className="col-4 mb-0">
                        <Form.Control
                            placeholder="Title"
                            className="autocomplete-input"
                            name="title"
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group as={Col} className="mb-0">
                        <Button type="submit" onClick={handleClick}>
                            Search
                        </Button>
                    </Form.Group>
                </Row>
            </Card>

            <Card className="mb-5 p-4">
                {dataLoading ? (
                    <TableSkeleton columns={GOAL_COLUMNS} rows={state.perPage} />
                ) : (
                    <Table hover responsive>
                        <thead>
                            <tr>
                                {GOAL_COLUMNS.map(column => (
                                    <th key={column.label} className={column.className}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {campaignGoal.map(goal => (
                                <tr key={goal.id}>
                                    <td>{goal.id}</td>
                                    <td>{goal.title}</td>
                                    <td>
                                        {goal.summary.length > 70
                                            ? goal.summary.substring(0, 69) + '...'
                                            : goal.summary}
                                    </td>
                                    <td>{goal.story.length > 70 ? goal.story.substring(0, 69) + '...' : goal.story}</td>
                                    <td>
                                        <ToggleChip
                                            name="isActive"
                                            onChange={() => handleCheckbox(goal.id as number)}
                                            checked={!!goal.isActive}
                                            label={goal.isActive ? 'Active' : 'Inactive'}
                                        />
                                    </td>
                                    <td>
                                        <img
                                            alt=""
                                            src={
                                                goal.goalImage ? goal?.goalImage?.thumbnail : '/images/placeholder.png'
                                            }
                                            style={{ width: 70, height: 60 }}
                                            className="rounded img-cover"
                                        />
                                    </td>
                                    <td>
                                        <NavLink
                                            className="btn btn-primary w-100 text-white"
                                            to={{
                                                pathname: '/campaign/goals/form/' + goal.id,
                                            }}
                                        >
                                            Edit
                                        </NavLink>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}

                <Pagination page={page} totalPages={totalPages} handlePagination={handlePages} disabled={dataLoading} />
            </Card>
        </>
    );
}
